import { useState } from "react";
import { supabase } from "@/lib/supabase";
import { useTours } from "@/lib/siteData";
import { money, useAdminRows, isUuid, fmtDateTime, numOrNull } from "@/lib/booking";
import { Card, Field, Select, SaveBtn, Ok, Err, MultiCheck, SubTitle, useAction, smallBtn } from "./adminUI";

const EMPTY = { code: "", discount_type: "percent", value: "", min_total: "", max_uses: "", starts_at: "", expires_at: "", tour_ids: [] };
const iso = (v) => (v ? new Date(v).toISOString() : null);

function couponState(c) {
    if (!c.active) return "Inativo";
    if (c.expires_at && new Date(c.expires_at) <= new Date()) return "Expirado";
    if (c.max_uses && c.times_used >= c.max_uses) return "Esgotado";
    return "Ativo";
}

export default function CouponsAdmin() {
    const tours = useTours(true).filter((t) => isUuid(t.id));
    const coupons = useAdminRows("coupons", (sb) => sb.from("booking_coupons").select("*").order("code"));
    const [f, setF] = useState(EMPTY);
    const { run, busy, msg, err } = useAction();
    const set = (k) => (v) => setF((o) => ({ ...o, [k]: v }));

    const create = () => run(async () => {
        const pct = f.discount_type === "percent";
        const r = await supabase.from("booking_coupons").insert({
            code: f.code.trim().toUpperCase(), discount_type: f.discount_type,
            discount_pct: pct ? Number(f.value) : 0, discount_fixed: pct ? 0 : Number(f.value),
            min_total: Number(f.min_total) || 0, max_uses: numOrNull(f.max_uses),
            starts_at: iso(f.starts_at), expires_at: iso(f.expires_at), tour_ids: f.tour_ids.length ? f.tour_ids : null,
        });
        if (!r.error) setF(EMPTY);
        return r;
    }, "Cupom criado");

    return (
        <Card className="space-y-5">
            <SubTitle>Cupons de desconto (validados no servidor)</SubTitle>
            <div className="grid gap-4 sm:grid-cols-3" data-testid="coupons-admin">
                <Field label="Código" value={f.code} onChange={(v) => set("code")(v.toUpperCase())} testid="coupon-code" />
                <Select label="Tipo" value={f.discount_type} onChange={set("discount_type")} options={[["percent", "Percentual (%)"], ["fixed", "Valor fixo (R$)"]]} testid="coupon-type" />
                <Field label={f.discount_type === "percent" ? "Desconto (%)" : "Desconto (R$)"} type="number" value={f.value} onChange={set("value")} testid="coupon-value" />
                <Field label="Valor mínimo (R$)" type="number" value={f.min_total} onChange={set("min_total")} />
                <Field label="Limite de usos" type="number" value={f.max_uses} onChange={set("max_uses")} />
                <Field label="Início (opcional)" type="datetime-local" value={f.starts_at} onChange={set("starts_at")} />
                <Field label="Validade (opcional)" type="datetime-local" value={f.expires_at} onChange={set("expires_at")} />
            </div>
            <MultiCheck label="Experiências elegíveis (vazio = todas)" options={tours.map((t) => [t.id, t.title])} value={f.tour_ids} onChange={set("tour_ids")} />
            <div className="flex items-center justify-end gap-3"><Ok msg={msg} /><Err msg={err} /><SaveBtn onClick={create} saving={busy} testid="coupon-create">Criar cupom</SaveBtn></div>
            {(coupons.data || []).map((c) => (
                <div key={c.code} className="flex flex-wrap items-center justify-between gap-3 border-t border-ink/10 pt-3" data-testid={`coupon-row-${c.code}`}>
                    <div><strong>{c.code}</strong>
                        <p className="text-xs text-dim">{c.discount_type === "fixed" ? money(c.discount_fixed) : `${c.discount_pct}%`} · usos {c.times_used}{c.max_uses ? `/${c.max_uses}` : ""}
                            {Number(c.min_total) ? ` · mín. ${money(c.min_total)}` : ""}{c.expires_at ? ` · até ${fmtDateTime(c.expires_at)}` : ""} · {couponState(c)}</p></div>
                    <button className={smallBtn} disabled={busy} onClick={() => run(() => supabase.from("booking_coupons").update({ active: !c.active }).eq("code", c.code))}>{c.active ? "Desativar" : "Ativar"}</button>
                </div>
            ))}
        </Card>
    );
}
